import axios from 'axios';
import * as R from 'ramda';
import React from 'react';

import { useCurrentLocale } from '@economic/react-app';
import templateReplacer from './templateReplacer';
import getEnvironment from '../../../../utilities/getEnvironment';
import tracer from '../../../../utilities/trace';
import routeTextsMap from '../../../../utilities/routeTextsMap';

// yes, a global!
let l10n = {};

export const t = (id, replacements: object | undefined = undefined): any => {
    const translation = l10n[id] || id;

    if (getEnvironment() === 'development') {
        if (translation === id && !!id) {
            tracer.warn(`Missing text id: ${id}`);
        }
    }

    return replacements ? templateReplacer(translation, replacements) : translation;
};

export const tByRoute = (route): any => {
    return t(routeTextsMap[route]);
};

interface TransProps {
    children?: any;
    id: string;
    replacements?: object;
}

export const Trans = ({ children: _, id, replacements }: TransProps) => <>{t(id, replacements)}</>;

export const useBaseL10n = (ids: string[], language?: string) => {
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const existingIds = Object.keys(l10n);
        const idsToFetch = R.difference(ids, existingIds);

        if (getEnvironment() === 'development') {
            tracer.group(`Fetched text ids`);
            tracer.info(`${idsToFetch.map(id => `'${id}'`).join(', ')}`);
            tracer.groupEnd();
        }

        if (idsToFetch.length) {
            axios.post(`${process.env.ECONOMIC_INTERNALAPI_PATH}/internal-api/texts/${language}`, ids).then(response => {
                l10n = { ...l10n, ...response.data };
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, []);

    return loading;
};

export const useLocalization = (ids: string[]): boolean => {
    const locale = useCurrentLocale();
    return useBaseL10n(ids, locale.languageCode);
};

export function mockLocalization(ids: string[]): void {
    l10n = ids.reduce(
        (result, id) => {
            result[id] = id;
            return result;
        },
        { ...l10n }
    );
}

/**
 * Translate text id and use BB style replacements
 *
 * @param {string} id
 * @param {(object | undefined)} [replacements=undefined]
 * @returns
 */
export function tBB(id: string, replacements: object | undefined = undefined) {
    // first get the translation from the ID
    const translation = l10n[id] || id;

    // extract BB info
    const [transformed, otherReplacements] = standardBB(translation);
    replacements = {
        ...otherReplacements,
        // ensure consumer can override replacements
        ...(replacements || {}),
    };

    return templateReplacer(transformed, replacements);
}

/**
 * Helper function that prepares BB style tags to be used with our own
 * replacement mechanism
 *
 * @export
 * @param {string} input
 * @returns {[string, object]}
 */
export function standardBB(input: string): [string, object] {
    let replaced = input;
    const replacements: Record<string, ((content: string) => string | React.ReactElement) | string> = {};

    // TODO: generalize for different tags and multiple instances
    const btn = extractTagContent(input, 'BUTTON');
    if (btn) {
        replacements.__BUTTON_CONTENT = btn;
        replaced = input.replace(/\[BUTTON\](.*?)\[\/BUTTON\]/i, '[__BUTTON]');
    }

    const link = extractTagContent(replaced, 'LINK');
    if (link) {
        replacements.__LINK_CONTENT = link;
        replaced = replaced.replace(/\[LINK\](.*?)\[\/LINK\]/i, '[__LINK]');
    }

    const em = extractTagContentAll(replaced, 'I');
    if (em) {
        em.forEach((emContent, i) => {
            replacements['__I_CONTENT' + i] = emContent;
            replacements['__I' + i] = content => <em>{content}</em>;
            replaced = replaced.replace(/\[I\](.*?)\[\/I\]/i, '[__I' + i + ']');
        });
    }

    const strong = extractTagContentAll(replaced, 'STRONG');
    if (strong) {
        strong.forEach((strongContent, i) => {
            replacements['__STRONG_CONTENT' + i] = strongContent;
            replacements['__STRONG' + i] = content => <strong>{content}</strong>;
            replaced = replaced.replace(/\[STRONG\](.*?)\[\/STRONG\]/i, '[__STRONG' + i + ']');
        });
    }

    const list = extractTagContent(replaced, 'LIST');
    if (list) {
        replaced = embedList(replaced);
    }

    return [replaced, replacements];
}

/**
 * Given a TAG, it will return the content _inside_ it.
 * @example
 * const result = extractTagContent('[TAG]content[/TAG]', 'TAG');
 * // result should be 'content'
 *
 * @export
 * @param {string} text
 * @param {string} tag
 * @returns
 */
export function extractTagContent(text: string, tag: string) {
    const linkRegex = new RegExp(`\\[${tag}\\](.*?)\\[/${tag}\\]`);
    const result = linkRegex.exec(text);

    if (result) {
        return result[1];
    }
    return undefined;
}

/**
 * Given a TAG, it will return an array containing the contents _inside_
 * all instances of said TAG.
 * @example
 * const result = extractTagContentAll('[TAG]content[/TAG] some text [TAG]second content[/TAG]', 'TAG');
 * // result should be ['content', 'second content']
 *
 * @export
 * @param {string} text
 * @param {string} tag
 * @returns
 */
export function extractTagContentAll(text: string, tag: string) {
    const linkRegex = new RegExp(`\\[${tag}\\](.*?)\\[/${tag}\\]`, 'g');
    const result: string[] = [];

    let partialResult;
    while ((partialResult = linkRegex.exec(text)) !== null) {
        result.push(partialResult[1]);
    }

    return result;
}

export function embedList(text: string) {
    try {
        const listContent = extractTagContent(text, 'LIST');

        if (listContent) {
            const temp = text.replace(/\[LIST\](.*?)\[\/LIST\]/i, '[REPLACEMENT]');
            return templateReplacer(temp, {
                REPLACEMENT: (
                    <ul className="pl-3 mt-3">
                        {listContent.split('|').map((l, index) => (
                            <li key={index}>{l}</li>
                        ))}
                    </ul>
                ),
            });
        }

        return text;
    } catch (e) {
        return text;
    }
}
